import React, { useState } from 'react';
import { apiFetch } from './utils/api';

function EmployeeDocumentsTab({ employeeId, documents, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [newDocument, setNewDocument] = useState({
    document_name: '',
    document_type: 'Contract',
    expiry_date: '',
    notes: '',
    file: null
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewDocument({
        ...newDocument,
        file: file,
        document_name: newDocument.document_name || file.name
      });
    }
  };

  const handleUpload = async () => {
    if (!newDocument.file) {
      alert('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', newDocument.file);
    formData.append('document_name', newDocument.document_name);
    formData.append('document_type', newDocument.document_type);
    formData.append('expiry_date', newDocument.expiry_date);
    formData.append('notes', newDocument.notes);

    try {
      const response = await apiFetch(`/api/employees/${employeeId}/documents`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        alert('Document uploaded successfully');
        setShowUploadForm(false);
        setNewDocument({
          document_name: '',
          document_type: 'Contract',
          expiry_date: '',
          notes: '',
          file: null
        });
        onRefresh();
      } else {
        alert('Error uploading document');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Error uploading document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (documentId, documentName) => {
    try {
      const response = await apiFetch(`/api/employees/documents/${documentId}/download`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error downloading document');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Delete this document?')) return;

    try {
      await apiFetch(`/api/employees/documents/${documentId}`, {
        method: 'DELETE'
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Error deleting document');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Documents</h2>
        <button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded transition-colors"
        >
          {showUploadForm ? 'Cancel' : '+ Upload Document'}
        </button>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Upload New Document</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Name</label>
              <input
                type="text"
                value={newDocument.document_name}
                onChange={(e) => setNewDocument({...newDocument, document_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
              <select
                value={newDocument.document_type}
                onChange={(e) => setNewDocument({...newDocument, document_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
              >
                <option value="Contract">Employment Contract</option>
                <option value="Policy">Company Policy</option>
                <option value="Certificate">Certificate</option>
                <option value="ID">ID Document</option>
                <option value="Right to Work">Right to Work</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (optional)</label>
              <input
                type="date"
                value={newDocument.expiry_date}
                onChange={(e) => setNewDocument({...newDocument, expiry_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
              <input
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={newDocument.notes}
                onChange={(e) => setNewDocument({...newDocument, notes: e.target.value})}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleUpload}
              disabled={uploading || !newDocument.file}
              className="bg-gray-300 hover:bg-gray-400 text-black px-6 py-2 rounded transition-colors disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </button>
          </div>
        </div>
      )}

      {/* Documents List */}
      {documents.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No documents uploaded</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-300">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Document Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Upload Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Expiry Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">File Size</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {documents.map((doc, idx) => (
                <tr key={doc.id} className={idx % 2 === 1 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="px-4 py-3 text-sm text-gray-900">{doc.document_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{doc.document_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(doc.upload_date)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(doc.expiry_date)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatFileSize(doc.file_size)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      doc.status === 'Current' ? 'bg-green-100 text-green-800' :
                      doc.status === 'Expired' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {doc.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm space-x-2">
                    <button
                      onClick={() => handleDownload(doc.id, doc.document_name)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Download
                    </button>
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default EmployeeDocumentsTab;
